import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Check, X, Trash2, CalendarDays, BellRing } from "lucide-react";

const MANAGER_ROLES = ["managing_director", "executive_director", "general_manager"];
const LEAVE_TYPES = ["annual", "sick", "maternity", "paternity", "compassionate", "study", "unpaid", "other"];
const STATUS_TONE = { pending: "amber", approved: "emerald", rejected: "red" };

export default function Leave() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", branch: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ staff_id: "", leave_type: "annual", start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.status) p.append("status", filters.status);
      if (filters.branch) p.append("branch", filters.branch);
      const [r, s] = await Promise.all([
        api.get(`/leave?${p.toString()}`),
        api.get("/leave/stats"),
      ]);
      setItems(r.data);
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);
  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    api.get("/attendance/enrollments").then(({ data }) => setStaffList(data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.staff_id || !form.start_date || !form.end_date) {
      toast.error("Pick a staff member and both dates"); return;
    }
    setBusy(true);
    try {
      await api.post("/leave", form);
      toast.success("Leave request submitted for approval");
      setOpen(false);
      setForm({ staff_id: "", leave_type: "annual", start_date: "", end_date: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decide = async (l, action) => {
    const comment = window.prompt(`${action === "approved" ? "Approve" : "Reject"} ${l.staff_name}'s ${l.leave_type} leave — optional comment:`) ?? "";
    try {
      await api.post(`/leave/${l.id}/decide`, { action, comment });
      toast.success(`Leave ${action}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async (l) => {
    if (!window.confirm(`Cancel this pending leave request for ${l.staff_name}?`)) return;
    try {
      await api.delete(`/leave/${l.id}`);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/attendance/run-absence-digest");
      const n = (data.sent || []).length;
      toast.success(n ? `Absence digest sent for ${n} branch(es)` : "No branches with staff to report");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDigestBusy(false); }
  };

  return (
    <div data-testid="leave-page">
      <PageHeader
        eyebrow="Administration"
        title="Leave Management"
        subtitle="Request and approve staff leave. Approved leave excuses absences in attendance reports and the morning absence digest."
        actions={
          <div className="flex gap-2">
            {isManager && (
              <Button variant="outline" size="sm" onClick={runDigest} disabled={digestBusy}
                data-testid="leave-run-digest">
                <BellRing size={14} className="mr-1.5" />
                {digestBusy ? "Sending…" : "Send absence digest now"}
              </Button>
            )}
            <Button size="sm" onClick={() => setOpen(true)}
              className="bg-emerald-900 hover:bg-emerald-800" data-testid="leave-request-button">
              <Plus size={14} className="mr-1.5" /> Request leave
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="leave-stats">
        <Badge tone="amber">Pending approval: {stats.pending ?? 0}</Badge>
        <Badge tone="emerald">On leave today: {stats.on_leave_today ?? 0}</Badge>
        <Badge tone="slate">Approved this month: {stats.approved_this_month ?? 0}</Badge>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filters.status} data-testid="leave-filter-status"
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
          <option value="">All statuses</option>
          {["pending", "approved", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {isManager && (
          <select value={filters.branch} data-testid="leave-filter-branch"
            onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}
            className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading leave requests…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No leave requests"
          hint="Click 'Request leave' to file one — managers approve or reject them here." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="leave-table">
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2.5">Staff</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Dates</th>
                <th className="px-3 py-2.5 text-center">Days</th>
                <th className="px-3 py-2.5">Reason</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                  data-testid={`leave-row-${l.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-gray-900">{l.staff_name}</div>
                    <div className="text-[11px] text-gray-400">{l.staff_number} · {l.branch || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 capitalize text-gray-700">{l.leave_type}</td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                    <CalendarDays size={12} className="inline mr-1 text-gray-400" />
                    {l.start_date} → {l.end_date}
                  </td>
                  <td className="px-3 py-2.5 text-center font-semibold text-gray-800">{l.days}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[200px] truncate">{l.reason || "—"}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={STATUS_TONE[l.status]}>{l.status}</Badge>
                    {l.decided_by_name && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        by {l.decided_by_name}{l.decision_comment ? ` — ${l.decision_comment}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1">
                      {isManager && l.status === "pending" && (
                        <>
                          <button onClick={() => decide(l, "approved")} title="Approve"
                            className="p-1.5 rounded text-emerald-700 hover:bg-emerald-50"
                            data-testid={`leave-approve-${l.id}`}>
                            <Check size={15} />
                          </button>
                          <button onClick={() => decide(l, "rejected")} title="Reject"
                            className="p-1.5 rounded text-red-600 hover:bg-red-50"
                            data-testid={`leave-reject-${l.id}`}>
                            <X size={15} />
                          </button>
                        </>
                      )}
                      {l.status === "pending" && (
                        <button onClick={() => cancel(l)} title="Cancel request"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`leave-cancel-${l.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="leave-dialog">
          <DialogHeader>
            <DialogTitle>Request leave</DialogTitle>
            <DialogDescription>Approved leave excuses the staff member in attendance reports.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Staff member</Label>
              <select value={form.staff_id} data-testid="leave-form-staff"
                onChange={(e) => setForm((f) => ({ ...f, staff_id: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm">
                <option value="">Select staff…</option>
                {staffList.map((s) => (
                  <option key={s.staff_id} value={s.staff_id}>{s.name} ({s.staff_number})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Leave type</Label>
              <select value={form.leave_type} data-testid="leave-form-type"
                onChange={(e) => setForm((f) => ({ ...f, leave_type: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm capitalize">
                {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">First day</Label>
                <Input type="date" value={form.start_date} data-testid="leave-form-start"
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Last day</Label>
                <Input type="date" value={form.end_date} data-testid="leave-form-end"
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="mt-1 h-10" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea value={form.reason} rows={2} placeholder="Optional"
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                data-testid="leave-form-reason" />
            </div>
            <Button onClick={submit} disabled={busy}
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="leave-form-submit">
              {busy ? "Submitting…" : "Submit for approval"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
